import {
  all,
  call,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { APIResponse } from 'spw-redux/types';
import moment from 'moment';
import { getProjects } from 'spw-api/projects';
import {
  getBenchmarkReportPreviewApi,
  getLocationsForBenchmarkProjectsApi,
  getBenchmarkProjectsByIdApi,
  getUSFRangeOfBenchmarkProjectsApi,
  getClientsOfBenchmarkProjectsApi,
  createBenchmarkReportApi,
  getBenchmarkReportApi,
  getBenchmarkReportsApi,
  addReportToFavoritesApi,
  removeReportFromFavoritesApi,
  editBenchmarkReportApi,
  deleteBenchmarkReportApi,
  verifyBenchmarkReportApi,
  updateBenchmarkReportProjectsApi,
  addBenchmarkReportEditorsApi,
  getBenchmarkReportEditorsApi,
  removeBenchmarkReportEditorApi,
  transferBenchmarkReportOwnershipApi,
  getMissingProjectDataApi,
  cloneBenchmarkReportApi,
  dismissBenchmarkIssuesApi,
  getMissingDataApi,
} from 'spw-api/benchmark-reports';
import { updateBenchmarkReportCategoryApi } from 'spw-api/benchmark-report-categories';
import { cloneDeep, pick } from 'lodash';
import { getOrganization } from 'spw-api';
import { fetchOrgSuccess } from 'spw-redux/modules/organization/actions';
import { updateBenchmarkReportIconsApi } from 'spw-api/benchmark-report-icons';
import { deleteAtIndex, replaceAtIndex } from 'spw-shared/utils';
import { IProject } from '../projects/types';
import {
  GET_BENCHMARK_PROJECTS,
  GET_LOCATIONS_FOR_BENCHMARK_REPORTS,
  GET_BENCHMARK_PROJECTS_USF_RANGE,
  getBenchmarkProjects,
  getBenchmarkProjectsFailed,
  getLocationsForBenchmarkProjectsSucceeded,
  getBenchmarkProjectsSucceeded,
  getUSFRangeOfBenchmarkProjectsSucceeded,
  GET_BENCHMARK_REPORT_PREVIEW,
  getBenchmarkReportPreviewSucceeded,
  getBenchmarkReportPreviewFailed,
  refreshSelectedBenchmarkProjectsSucceeded,
  REFRESH_SELECTED_BENCHMARK_PROJECTS,
  getClientsOfBenchmarkProjectsSucceeded,
  getClientsOfBenchmarkProjectsFailed,
  GET_CLIENTS_OF_BENCHMARK_PROJECTS,
  CREATE_BENCHMARK_REPORT,
  createBenchmarkReport,
  getBenchmarkReport,
  GET_BENCHMARK_REPORT,
  getBenchmarkReportSucceeded,
  getBenchmarkReports,
  GET_BENCHMARK_REPORTS,
  getBenchmarkReportsSucceeded,
  toggleReportFromFavorites,
  TOGGLE_BENCHMARK_REPORT_FROM_FAVORITES,
  setBenchmarkReports,
  editBenchmarkReport,
  editBenchmarkReportSucceeded,
  EDIT_BENCHMARK_REPORT,
  deleteBenchmarkReport,
  DELETE_BENCHMARK_REPORT,
  cleanUpCurrentBenchmarkReport,
  setSelectedBenchmarkProjects,
  verifyBenchmarkReport,
  VERIFY_BENCHMARK_REPORT,
  saveBenchmarkReportChanges,
  SAVE_BENCHMARK_REPORT_CHANGES,
  addBenchmarkReportEditors,
  ADD_BENCHMARK_REPORT_EDITORS,
  getBenchmarkReportEditors,
  getBenchmarkReportEditorsSucceeded,
  GET_BENCHMARK_REPORT_EDITORS,
  removeBenchmarkReportEditor,
  REMOVE_BENCHMARK_REPORT_EDITOR,
  highlightReportRow,
  transferBenchmarkReportOwnership,
  TRANSFER_BENCHMARK_REPORT_OWNERSHIP,
  saveBenchmarkReportChangesSucceeded,
  GET_BENCHMARK_REPORT_SUCCEEDED,
  getBenchmarkReportForExportable,
  getBenchmarkReportForExportableSucceded,
  GET_BENCHMARK_REPORT_FOR_EXPORTABLE,
  getMissingProjectData,
  getMissingProjectDataSucceeded,
  GET_MISSING_PROJECT_DATA,
  UPDATE_BENCHMARK_REPORT_CATEGORIES,
  updateBenchmarkReportCategories,
  updateBenchmarkReportIcons,
  UPDATE_BENCHMARK_REPORT_ICONS,
  cloneBenchmarkReport,
  CLONE_BENCHMARK_REPORT,
  dismissBenchmarkIssues,
  dismissBenchmarkIssuesSucceded,
  DISMISS_BENCHMARK_ISSUES,
  updateBenchmarkReportCategoriesSucceded,
  updateBenchmarkReportIconsSucceeded,
  getMissingData,
  GET_MISSING_DATA,
  getMissingDataSucceeded,
  DELETE_MISSING_FLOOR_DATA_FIELD,
  deleteMissingFloorDataField,
  addMissingFloorDataField,
  ADD_MISSING_FLOOR_DATA_FIELD,
  updateMissingFloorDataField,
  UPDATE_MISSING_FLOOR_DATA_FIELD,
} from './actions';
import { handleError, showToastMessage } from '../UI/messages/actions';
import {
  selectBenchmarkReports,
  selectCurrentBechmarkReport,
  selectCurrentBenchmarkReportCategoriesMap,
  selectMissingData,
  selectSelectedBenchmarkProjects,
} from './selectors';
import {
  BenchmarkProject,
  BenchmarkReport,
  BenchmarkReportCategory,
  MissingBuildingData,
  MissingData,
} from './types';
import { IUser } from '../user/types';
import { currentUser as selectCurrentUser } from '../user/selectors';
import { setUserFavoriteReport } from '../users/actions';

function* getBenchmarkProjectsCall(
  action: ReturnType<typeof getBenchmarkProjects>
) {
  try {
    const projectResponse: APIResponse<typeof getProjects> = yield call(
      getProjects,
      action.payload
    );

    const {
      projects: { projects },
    } = projectResponse.data;

    const projectsWithInsertedAt = projects.map((project: IProject) => ({
      ...project,
      insertedAt: moment.utc(project.insertedAt, 'YYYY-MM-DD HH:mm:ss').local(),
    }));
    yield put(getBenchmarkProjectsSucceeded(projectsWithInsertedAt));
  } catch (e) {
    yield put(handleError(e));
    yield put(getBenchmarkProjectsFailed());
  }
}

function* getLocationsForBenchmarkProjectsCall() {
  try {
    const response: APIResponse<typeof getLocationsForBenchmarkProjectsApi> = yield call(
      getLocationsForBenchmarkProjectsApi
    );

    yield put(
      getLocationsForBenchmarkProjectsSucceeded(response.data.locations)
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getUSFRangeOfBenchmarkProjectsCall() {
  try {
    const response: APIResponse<typeof getUSFRangeOfBenchmarkProjectsApi> = yield call(
      getUSFRangeOfBenchmarkProjectsApi
    );

    yield put(getUSFRangeOfBenchmarkProjectsSucceeded(response.data.range));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getBenchmarkReportPreviewCall() {
  try {
    const selectedProjects: BenchmarkProject[] = yield select(
      selectSelectedBenchmarkProjects
    );
    const linkedProjects = selectedProjects?.filter((brp) => brp.linked);
    const linkedProjectIds = linkedProjects.map((lp) => lp.project.id);
    const response: APIResponse<typeof getBenchmarkReportPreviewApi> = yield call(
      getBenchmarkReportPreviewApi,
      linkedProjectIds
    );

    yield put(getBenchmarkReportPreviewSucceeded(response.data.summary));
  } catch (e) {
    yield put(getBenchmarkReportPreviewFailed());
    yield put(handleError(e));
  }
}

function* refreshSelectedBenchmarkProjectsCall() {
  try {
    const selectedProjects: BenchmarkProject[] = yield select(
      selectSelectedBenchmarkProjects
    );

    const ids = selectedProjects?.map((lp) => lp.project.id);
    if (!ids?.length) return;
    const response: APIResponse<typeof getBenchmarkProjectsByIdApi> = yield call(
      getBenchmarkProjectsByIdApi,
      ids
    );

    const { projects: freshSelectedProjects } = response.data.projects;
    const selectedProjectsMap: Record<
      number,
      IProject
    > = freshSelectedProjects.reduce((map, project) => {
      return {
        ...map,
        [project.id]: project,
      };
    }, {});

    const newSelectedProjects = cloneDeep(selectedProjects).map((bp) => {
      return { ...bp, project: selectedProjectsMap[bp.project.id] };
    });

    yield put(refreshSelectedBenchmarkProjectsSucceeded(newSelectedProjects));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getClientsOfBenchmarkProjectsCall() {
  try {
    const response: APIResponse<typeof getClientsOfBenchmarkProjectsApi> = yield call(
      getClientsOfBenchmarkProjectsApi
    );
    yield put(getClientsOfBenchmarkProjectsSucceeded(response.data.clients));
  } catch (e) {
    yield put(handleError(e));
    yield put(getClientsOfBenchmarkProjectsFailed());
  }
}

function* createBenchmarkReportCall(
  action: ReturnType<typeof createBenchmarkReport>
) {
  const selectedProjects: BenchmarkProject[] = yield select(
    selectSelectedBenchmarkProjects
  );
  const currentUser: IUser = yield select(selectCurrentUser);
  const report: BenchmarkReport = {
    name: action.payload.name.trim(),
    categories: action.payload.categories,
    icons: action.payload.icons,
    benchmarkProjects: selectedProjects.map((brProject) => ({
      projectId: brProject.project.id,
      linked: brProject.linked,
    })),
  };

  try {
    const response: APIResponse<typeof createBenchmarkReportApi> = yield call(
      createBenchmarkReportApi,
      report
    );

    if (!currentUser.isOrgAdmin) {
      yield put(
        showToastMessage({
          status: 'success',
          text: 'The report was successfully saved!',
        })
      );
    }
    yield put(getBenchmarkReport(response.data.report.id));
    yield take(GET_BENCHMARK_REPORT_SUCCEEDED);
    yield put(highlightReportRow(response.data.report.id));
    if (action.payload.onSuccess)
      action.payload.onSuccess(response.data.report);
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getBenchmarkReportCall(
  action: ReturnType<typeof getBenchmarkReport>
) {
  try {
    const currentReport: BenchmarkReport = yield select(
      selectCurrentBechmarkReport
    );

    if (currentReport && currentReport.id !== action.payload)
      yield put(cleanUpCurrentBenchmarkReport());

    const response: APIResponse<typeof getBenchmarkReportApi> = yield call(
      getBenchmarkReportApi,
      action.payload
    );

    yield put(getBenchmarkReportSucceeded(response.data.report));
    if (!currentReport || currentReport.id !== response.data.report.id) {
      yield put(
        setSelectedBenchmarkProjects(response.data.report.benchmarkProjects)
      );
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getBenchmarkReportForExportableCall(
  action: ReturnType<typeof getBenchmarkReportForExportable>
) {
  try {
    const { reportId, linkedProjectsIds } = action.payload;

    const {
      data: { report },
    }: APIResponse<typeof getBenchmarkReportApi> = yield call(
      getBenchmarkReportApi,
      reportId
    );

    const {
      data: { organization },
    }: APIResponse<typeof getOrganization> = yield call(
      getOrganization,
      report.organizationId
    );

    yield put(fetchOrgSuccess(organization));

    const {
      data: {
        projects: { projects: linkedProjects },
      },
    }: APIResponse<typeof getBenchmarkProjectsByIdApi> = yield call(
      getBenchmarkProjectsByIdApi,
      linkedProjectsIds
    );
    const selectedProjects = linkedProjects.map(
      (p) => ({ project: p, linked: true } as BenchmarkProject)
    );
    const {
      data: { summary },
    }: APIResponse<typeof getBenchmarkReportPreviewApi> = yield call(
      getBenchmarkReportPreviewApi,
      linkedProjectsIds
    );
    yield put(
      getBenchmarkReportForExportableSucceded({
        report,
        selectedProjects,
        reportSummary: summary,
      })
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getBenchmarkReportsCall(
  action: ReturnType<typeof getBenchmarkReports>
) {
  try {
    const response: APIResponse<typeof getBenchmarkReportsApi> = yield call(
      getBenchmarkReportsApi,
      action.payload
    );
    yield put(getBenchmarkReportsSucceeded(response.data.paginatedReports));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* toggleReportFromFavoritesCall(
  action: ReturnType<typeof toggleReportFromFavorites>
) {
  try {
    const { isMarkedAsFavoriteByUser, reportId, onSuccess } = action.payload;
    if (isMarkedAsFavoriteByUser) {
      yield call(addReportToFavoritesApi, reportId);
      yield put(
        showToastMessage({
          status: 'success',
          text: 'The benchmark report has been added to your favorites!',
        })
      );
    } else {
      yield call(removeReportFromFavoritesApi, reportId);
    }
    const currentReport: BenchmarkReport = yield select(
      selectCurrentBechmarkReport
    );
    if (currentReport?.id === reportId) {
      yield put(
        getBenchmarkReportSucceeded({
          ...currentReport,
          isMarkedAsFavoriteByUser,
        })
      );
    }
    // update all benchmark reports list
    const benchmarkReports: BenchmarkReport[] = yield select(
      selectBenchmarkReports
    );
    const newBenchmarkReports = cloneDeep(benchmarkReports);
    const updatedReportIndex = newBenchmarkReports?.findIndex(
      (br) => br.id === reportId
    );
    if (newBenchmarkReports && updatedReportIndex !== -1)
      newBenchmarkReports[
        updatedReportIndex
      ].isMarkedAsFavoriteByUser = isMarkedAsFavoriteByUser;
    yield put(setBenchmarkReports(newBenchmarkReports));

    // update user benchmark reports list
    yield put(setUserFavoriteReport(reportId, isMarkedAsFavoriteByUser));
    if (onSuccess) onSuccess();
  } catch (e) {
    yield put(handleError(e));
  }
}

function* editBenchmarkReportCall(
  action: ReturnType<typeof editBenchmarkReport>
) {
  try {
    const response: APIResponse<typeof editBenchmarkReportApi> = yield call(
      editBenchmarkReportApi,
      action.payload
    );

    yield put(editBenchmarkReportSucceeded(response.data.editedReport));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* deleteBenchmarkReportCall(
  action: ReturnType<typeof deleteBenchmarkReport>
) {
  try {
    yield call(deleteBenchmarkReportApi, action.payload.reportId);
    if (action.payload.onSuccess) action.payload.onSuccess();
  } catch (e) {
    yield put(handleError(e));
  }
}

function* verifyBenchmarkReportCall(
  action: ReturnType<typeof verifyBenchmarkReport>
) {
  try {
    const currentReport: BenchmarkReport = yield select(
      selectCurrentBechmarkReport
    );
    const response: APIResponse<typeof verifyBenchmarkReportApi> = yield call(
      verifyBenchmarkReportApi,
      currentReport.id
    );
    yield put(highlightReportRow(response.data.report.id));
    const { onSuccess } = action.payload;
    yield put(
      showToastMessage({
        status: 'success',
        text: 'The report was successfully verified!',
      })
    );
    if (onSuccess) onSuccess();
  } catch (e) {
    yield put(handleError(e));
  }
}

function* saveBenchmarkReportChangesCall(
  action: ReturnType<typeof saveBenchmarkReportChanges>
) {
  try {
    const currentUser: IUser = yield select(selectCurrentUser);
    const currentReport: BenchmarkReport = yield select(
      selectCurrentBechmarkReport
    );
    const selectedProjects: BenchmarkProject[] = yield select(
      selectSelectedBenchmarkProjects
    );
    const projects = selectedProjects.map((p) =>
      pick(p, ['linked', 'projectId'])
    );
    yield call(updateBenchmarkReportProjectsApi, currentReport.id, projects);
    yield put(
      editBenchmarkReportSucceeded({ ...currentReport, verified: false })
    );
    yield put(saveBenchmarkReportChangesSucceeded(selectedProjects));
    yield put(highlightReportRow(currentReport.id));
    if (!currentUser.isOrgAdmin) {
      yield put(
        showToastMessage({
          status: 'success',
          text: 'The report was successfully saved!',
        })
      );
    }

    const { onSuccess } = action.payload;
    if (onSuccess) onSuccess();
  } catch (e) {
    yield put(handleError(e));
  }
}

function* addBenchmarkReportEditorsCall(
  action: ReturnType<typeof addBenchmarkReportEditors>
) {
  const { onSuccess, usersIds } = action.payload;
  try {
    const currentReport: BenchmarkReport = yield select(
      selectCurrentBechmarkReport
    );

    const response: APIResponse<typeof addBenchmarkReportEditorsApi> = yield call(
      addBenchmarkReportEditorsApi,
      {
        reportId: currentReport.id,
        usersIds,
      }
    );

    if (onSuccess) onSuccess();
    yield put(getBenchmarkReportEditorsSucceeded(response.data.reportEditors));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getBenchmarkReportEditorsCall(
  action: ReturnType<typeof getBenchmarkReportEditors>
) {
  try {
    const response: APIResponse<typeof getBenchmarkReportEditorsApi> = yield call(
      getBenchmarkReportEditorsApi,
      action.payload
    );
    yield put(getBenchmarkReportEditorsSucceeded(response.data.reportEditors));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* removeBenchmarkReportEditorCall(
  action: ReturnType<typeof removeBenchmarkReportEditor>
) {
  const { onSuccess, userId, reportId } = action.payload;
  try {
    const response: APIResponse<typeof removeBenchmarkReportEditorApi> = yield call(
      removeBenchmarkReportEditorApi,
      {
        reportId,
        userId,
      }
    );

    if (onSuccess) onSuccess();
    yield put(getBenchmarkReportEditorsSucceeded(response.data.reportEditors));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* transferBenchmarkReportOwnershipCall(
  action: ReturnType<typeof transferBenchmarkReportOwnership>
) {
  const {
    onSuccess,
    newOwnerId,
    shouldKeepOwnerAsEditor,
    reportId,
  } = action.payload;
  try {
    const response: APIResponse<typeof transferBenchmarkReportOwnershipApi> = yield call(
      transferBenchmarkReportOwnershipApi,
      {
        reportId,
        newOwnerId,
        shouldKeepOwnerAsEditor,
      }
    );
    yield put(editBenchmarkReportSucceeded(response.data.report));
    yield put(getBenchmarkReportEditors(reportId));
    if (onSuccess) onSuccess();
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getMissingDataCall(action: ReturnType<typeof getMissingData>) {
  try {
    const response: APIResponse<typeof getMissingDataApi> = yield call(
      getMissingDataApi,
      action.payload
    );
    yield put(getMissingDataSucceeded(response.data.missingProjectData));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* addMissingFloorDataFieldCall(
  action: ReturnType<typeof addMissingFloorDataField>
) {
  try {
    const { buildingId, floorId, missingDataField } = action.payload;
    const missingData: MissingData = yield select(selectMissingData);

    if (!missingData) return;

    const missingBuildingDataIndex = missingData?.missingBuildingData?.findIndex(
      (missingBuildingData) => missingBuildingData.buildingId === buildingId
    );

    if (missingBuildingDataIndex >= 0) {
      const missingBuildingData =
        missingData.missingBuildingData[missingBuildingDataIndex];

      const missingFloorDataIndex = missingBuildingData.missingFloorData.findIndex(
        (missingFloorData) => missingFloorData.floorId === floorId
      );

      const missingFloorData =
        missingFloorDataIndex >= 0
          ? missingBuildingData.missingFloorData[missingFloorDataIndex]
          : { floorId, missingFields: [] };

      const newMissingFloorData = {
        ...missingFloorData,
        missingFields: [...missingFloorData.missingFields, missingDataField],
      };

      const newMissingBuildingData = {
        ...missingBuildingData,
        missingFloorData:
          missingFloorDataIndex >= 0
            ? replaceAtIndex(
                missingBuildingData.missingFloorData,
                newMissingFloorData,
                missingFloorDataIndex
              )
            : [...missingBuildingData.missingFloorData, newMissingFloorData],
      };

      const newMissingData: MissingData = {
        ...missingData,
        missingBuildingData: replaceAtIndex(
          missingData.missingBuildingData,
          newMissingBuildingData,
          missingBuildingDataIndex
        ) as MissingBuildingData[],
      };

      yield put(getMissingDataSucceeded(newMissingData));
    } else {
      const newMissingData = {
        ...missingData,
        missingBuildingData: [
          ...missingData.missingBuildingData,
          {
            buildingId,
            missingFloorData: [
              {
                floorId,
                missingFields: [missingDataField],
              },
            ],
          },
        ],
      };

      yield put(getMissingDataSucceeded(newMissingData));
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateMissingFloorDataFieldCall(
  action: ReturnType<typeof updateMissingFloorDataField>
) {
  try {
    const { buildingId, floorId, missingDataField } = action.payload;
    const missingData: MissingData = yield select(selectMissingData);

    if (!missingData) return;

    const missingBuildingDataIndex = missingData?.missingBuildingData?.findIndex(
      (missingBuildingData) => missingBuildingData.buildingId === buildingId
    );

    if (missingBuildingDataIndex >= 0) {
      const missingBuildingData =
        missingData.missingBuildingData[missingBuildingDataIndex];

      const missingFloorDataIndex = missingBuildingData.missingFloorData.findIndex(
        (missingFloorData) => missingFloorData.floorId === floorId
      );

      if (missingFloorDataIndex < 0) return;

      const missingFloorData =
        missingBuildingData.missingFloorData[missingFloorDataIndex];

      const missingFieldIndex = missingFloorData.missingFields.findIndex(
        (missingField) => missingField.id === missingDataField.id
      );

      if (missingFieldIndex < 0) return;

      const newMissingFields = replaceAtIndex(
        missingFloorData.missingFields,
        missingDataField,
        missingFieldIndex
      );

      const newMissingFloorData = {
        ...missingFloorData,
        missingFields: newMissingFields,
      };

      const newMissingBuildingData = {
        ...missingBuildingData,
        missingFloorData: replaceAtIndex(
          missingBuildingData.missingFloorData,
          newMissingFloorData,
          missingFloorDataIndex
        ),
      };

      const newMissingData: MissingData = {
        ...missingData,
        missingBuildingData: replaceAtIndex(
          missingData.missingBuildingData,
          newMissingBuildingData,
          missingBuildingDataIndex
        ) as MissingBuildingData[],
      };

      yield put(getMissingDataSucceeded(newMissingData));
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* deleteMissingFloorDataFieldCall(
  action: ReturnType<typeof deleteMissingFloorDataField>
) {
  try {
    const { buildingId, floorId, missingDataFieldId } = action.payload;
    const missingData: MissingData = yield select(selectMissingData);

    if (missingData) {
      const missingBuildingDataIndex = missingData?.missingBuildingData?.findIndex(
        (missingBuildingData) => missingBuildingData.buildingId === buildingId
      );

      if (missingBuildingDataIndex >= 0) {
        const missingBuildingData =
          missingData.missingBuildingData[missingBuildingDataIndex];

        const missingFloorDataIndex = missingBuildingData.missingFloorData.findIndex(
          (missingFloorData) => missingFloorData.floorId === floorId
        );

        if (missingFloorDataIndex < 0) {
          return;
        }

        const missingFloorData =
          missingBuildingData.missingFloorData[missingFloorDataIndex];

        const newMissingFields = missingFloorData.missingFields.filter(
          (missingField) => missingField.id !== missingDataFieldId
        );

        const newMissingFloorData = {
          ...missingFloorData,
          missingFields: newMissingFields,
        };

        const newMissingBuildingData = {
          ...missingBuildingData,
          missingFloorData: newMissingFields?.length
            ? replaceAtIndex(
                missingBuildingData.missingFloorData,
                newMissingFloorData,
                missingFloorDataIndex
              )
            : deleteAtIndex(
                missingBuildingData.missingFloorData,
                missingFloorDataIndex
              ),
        };

        const newMissingData = {
          ...missingData,
          missingBuildingData: newMissingBuildingData.missingFloorData?.length
            ? replaceAtIndex(
                missingData.missingBuildingData,
                newMissingBuildingData,
                missingBuildingDataIndex
              )
            : deleteAtIndex(
                missingData.missingBuildingData,
                missingBuildingDataIndex
              ),
        };

        yield put(getMissingDataSucceeded({ ...newMissingData }));
      }
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getMissingProjectDataCall(
  action: ReturnType<typeof getMissingProjectData>
) {
  try {
    const response: APIResponse<typeof getMissingProjectDataApi> = yield call(
      getMissingProjectDataApi,
      action.payload
    );
    yield put(getMissingProjectDataSucceeded(response.data.missingProjectData));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* dismissBenchmarkIssuesCall(
  action: ReturnType<typeof dismissBenchmarkIssues>
) {
  try {
    const response: APIResponse<typeof dismissBenchmarkIssuesApi> = yield call(
      dismissBenchmarkIssuesApi,
      action.payload.projectId,
      action.payload.dismissedIssues
    );
    yield put(dismissBenchmarkIssuesSucceded(response.data.missingProjectData));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateBenchmarkReportCategoriesCall(
  action: ReturnType<typeof updateBenchmarkReportCategories>
) {
  try {
    const { benchmarkReportCategories, onSuccess } = action.payload;
    const currentBenchmarkReportCategoriesMap: Record<
      string,
      BenchmarkReportCategory
    > = yield select(selectCurrentBenchmarkReportCategoriesMap);

    const {
      data: { reportCategories },
    }: APIResponse<typeof updateBenchmarkReportCategoryApi> = yield call(
      updateBenchmarkReportCategoryApi,
      benchmarkReportCategories
    );

    const updatedReportCategories = reportCategories.reduce(
      (categories, updatedCategory) => {
        const { defaultCategory } = updatedCategory;

        return {
          ...categories,
          [defaultCategory]: {
            ...categories[defaultCategory],
            ...updatedCategory,
          },
        };
      },
      currentBenchmarkReportCategoriesMap
    );

    yield put(
      updateBenchmarkReportCategoriesSucceded(
        Object.values(updatedReportCategories)
      )
    );

    if (onSuccess) {
      onSuccess();
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateBenchmarkReportIconsCall(
  action: ReturnType<typeof updateBenchmarkReportIcons>
) {
  try {
    const currentReport: BenchmarkReport = yield select(
      selectCurrentBechmarkReport
    );

    const {
      data: { reportIcons },
    }: APIResponse<typeof updateBenchmarkReportIconsApi> = yield call(
      updateBenchmarkReportIconsApi,
      action.payload,
      currentReport.id
    );

    yield put(updateBenchmarkReportIconsSucceeded(reportIcons));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* cloneBenchmarkReportCall(
  action: ReturnType<typeof cloneBenchmarkReport>
) {
  const currentUser: IUser = yield select(selectCurrentUser);
  const { srcReportId, report } = action.payload;

  try {
    const response: APIResponse<typeof cloneBenchmarkReportApi> = yield call(
      cloneBenchmarkReportApi,
      srcReportId,
      report
    );

    if (!currentUser.isOrgAdmin) {
      yield put(
        showToastMessage({
          status: 'success',
          text: 'The report was successfully saved!',
        })
      );
    }

    yield put(highlightReportRow(response.data.report.id));
    if (action.payload.onSuccess) action.payload.onSuccess();
  } catch (e) {
    yield put(handleError(e));
  }
}

function* saga() {
  yield all([
    takeLatest(GET_BENCHMARK_PROJECTS, getBenchmarkProjectsCall),
    takeLatest(
      GET_LOCATIONS_FOR_BENCHMARK_REPORTS,
      getLocationsForBenchmarkProjectsCall
    ),
    takeLatest(
      GET_BENCHMARK_PROJECTS_USF_RANGE,
      getUSFRangeOfBenchmarkProjectsCall
    ),
    takeLatest(GET_BENCHMARK_REPORT_PREVIEW, getBenchmarkReportPreviewCall),
    takeLatest(
      REFRESH_SELECTED_BENCHMARK_PROJECTS,
      refreshSelectedBenchmarkProjectsCall
    ),
    takeLatest(
      GET_CLIENTS_OF_BENCHMARK_PROJECTS,
      getClientsOfBenchmarkProjectsCall
    ),
    takeLatest(CREATE_BENCHMARK_REPORT, createBenchmarkReportCall),
    takeLatest(GET_BENCHMARK_REPORT, getBenchmarkReportCall),
    takeLatest(
      GET_BENCHMARK_REPORT_FOR_EXPORTABLE,
      getBenchmarkReportForExportableCall
    ),
    takeLatest(GET_BENCHMARK_REPORTS, getBenchmarkReportsCall),
    takeLatest(
      TOGGLE_BENCHMARK_REPORT_FROM_FAVORITES,
      toggleReportFromFavoritesCall
    ),
    takeLatest(EDIT_BENCHMARK_REPORT, editBenchmarkReportCall),
    takeLatest(DELETE_BENCHMARK_REPORT, deleteBenchmarkReportCall),
    takeLatest(VERIFY_BENCHMARK_REPORT, verifyBenchmarkReportCall),
    takeLatest(SAVE_BENCHMARK_REPORT_CHANGES, saveBenchmarkReportChangesCall),
    takeLatest(ADD_BENCHMARK_REPORT_EDITORS, addBenchmarkReportEditorsCall),
    takeLatest(GET_BENCHMARK_REPORT_EDITORS, getBenchmarkReportEditorsCall),
    takeLatest(REMOVE_BENCHMARK_REPORT_EDITOR, removeBenchmarkReportEditorCall),
    takeLatest(
      TRANSFER_BENCHMARK_REPORT_OWNERSHIP,
      transferBenchmarkReportOwnershipCall
    ),
    takeLatest(GET_MISSING_PROJECT_DATA, getMissingProjectDataCall),
    takeLatest(GET_MISSING_DATA, getMissingDataCall),
    takeEvery(ADD_MISSING_FLOOR_DATA_FIELD, addMissingFloorDataFieldCall),
    takeEvery(UPDATE_MISSING_FLOOR_DATA_FIELD, updateMissingFloorDataFieldCall),
    takeEvery(DELETE_MISSING_FLOOR_DATA_FIELD, deleteMissingFloorDataFieldCall),
    takeLatest(
      UPDATE_BENCHMARK_REPORT_CATEGORIES,
      updateBenchmarkReportCategoriesCall
    ),
    takeLatest(UPDATE_BENCHMARK_REPORT_ICONS, updateBenchmarkReportIconsCall),
    takeLatest(CLONE_BENCHMARK_REPORT, cloneBenchmarkReportCall),
    takeLatest(DISMISS_BENCHMARK_ISSUES, dismissBenchmarkIssuesCall),
  ]);
}

export default saga;
